import { type ReactNode } from "react";
import { Flex, LoadingIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import * as styles from "../styles_vanilla.css";

type PreviewPanelProps = {
  pdfUrl: string | null;
  isLoading?: boolean;
  isRendering: boolean;
  error: string | null;
  errorTitle?: string;
  placeholder: ReactNode;
  panelClassName?: string;
  pdfViewerClassName?: string;
};

export const PreviewPanel = ({
  pdfUrl,
  isLoading = false,
  isRendering,
  error,
  errorTitle = "Preview failed",
  placeholder,
  panelClassName = styles.previewPanel,
  pdfViewerClassName = styles.pdfViewer,
}: PreviewPanelProps) => {
  if (isLoading || isRendering) {
    return (
      <Flex alignItems="center" justifyContent="center" className={panelClassName}>
        <LoadingIndicator />
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={8}
        className={panelClassName}
      >
        <Text variant="h3">{errorTitle}</Text>
        <Text variant="secondary-md">{error}</Text>
      </Flex>
    );
  }

  if (pdfUrl) {
    return (
      <Flex flexDirection="column" className={panelClassName}>
        <iframe src={pdfUrl} title="Template Preview" className={pdfViewerClassName} />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      className={panelClassName}
    >
      {placeholder}
    </Flex>
  );
};

type PreviewPlaceholderProps = {
  title: string;
  description: string;
};

export const PreviewPlaceholder = ({ title, description }: PreviewPlaceholderProps) => (
  <Flex flexDirection="column" alignItems="center" justifyContent="center" gap={8}>
    <Icon
      type="search"
      size="xxx-large"
      color={Colors.Neutral700}
      className={styles.previewIcon}
    />
    <Text variant="h3">{title}</Text>
    <Text variant="secondary-md">{description}</Text>
  </Flex>
);
